import { supabase } from '@/integrations/supabase/client';

// Interfaces for real analytics data
export interface RealPlayerStat {
  player_id: string;
  player_type: 'supabase' | 'internal';
  total_points: number;
  total_assists: number;
  total_goals: number;
  total_rebounds: number;
  total_saves: number;
  total_shots: number;
  total_fouls: number;
  games_played: number;
  games_won: number;
  games_lost: number;
  games_drawn: number;
  win_rate: number;
  avg_points: number;
  avg_assists: number;
  avg_goals: number;
}

export interface RealTeamAnalytics {
  team_id: number;
  sport_id: number;
  season_year: number;
  total_games: number;
  games_won: number;
  games_lost: number;
  games_drawn: number;
  win_rate: number;
  total_points_scored: number;
  total_points_conceded: number;
  avg_points_per_game: number;
  recent_form: any[];
  monthly_performance: any[];
}

export interface RealUserActivity {
  user_id: string;
  user_type: 'supabase' | 'internal';
  total_logins: number;
  total_games_participated: number;
  total_teams_joined: number;
  total_messages_sent: number;
  engagement_score: number;
  activity_level: 'low' | 'medium' | 'high' | 'very_high';
  last_login_at: string;
}

export interface UnifiedUser {
  id: string;
  user_type: 'supabase' | 'internal';
  username?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  display_name?: string;
}

class AnalyticsService {
  // Get real overview statistics
  async getOverviewStatistics() {
    try {
      const [
        totalGames,
        activeUsers,
        completedGames,
        totalGoals
      ] = await Promise.all([
        this.getTotalGamesCount(),
        this.getActiveUsersCount(), 
        this.getCompletedGamesData(),
        this.getTotalGoalsScored()
      ]);

      return {
        totalGames: totalGames.count,
        totalGamesThisMonth: totalGames.thisMonth,
        winRate: completedGames.winRate,
        winRateChange: completedGames.winRateChange,
        goalsScored: totalGoals.total,
        goalsPerGame: totalGoals.perGame,
        activePlayers: activeUsers.count,
        newPlayersThisMonth: activeUsers.newThisMonth
      };
    } catch (error) {
      console.error('Error getting overview statistics:', error);
      return {
        totalGames: 0,
        totalGamesThisMonth: 0,
        winRate: 0,
        winRateChange: 0,
        goalsScored: 0,
        goalsPerGame: 0,
        activePlayers: 0,
        newPlayersThisMonth: 0
      };
    }
  }

  private async getTotalGamesCount() {
    try {
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      
      // Count from both games and venue_games tables
      const [{ count: gamesCount }, { count: venueGamesCount }] = await Promise.all([
        supabase.from('games').select('*', { count: 'exact', head: true }),
        supabase.from('venue_games').select('*', { count: 'exact', head: true })
      ]);

      const [{ count: gamesThisMonth }, { count: venueGamesThisMonth }] = await Promise.all([
        supabase.from('games')
          .select('*', { count: 'exact', head: true })
          .gte('created_at', startOfMonth.toISOString()),
        supabase.from('venue_games')
          .select('*', { count: 'exact', head: true })
          .gte('created_at', startOfMonth.toISOString())
      ]);

      return {
        count: (gamesCount || 0) + (venueGamesCount || 0),
        thisMonth: (gamesThisMonth || 0) + (venueGamesThisMonth || 0)
      };
    } catch (error) {
      console.error('Error getting total games count:', error);
      return { count: 0, thisMonth: 0 };
    }
  }

  private async getActiveUsersCount() {
    try {
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      
      // Count Supabase users
      const [{ count: supabaseCount }, { count: internalCount }] = await Promise.all([
        supabase.from('profiles').select('*', { count: 'exact', head: true }),
        supabase.from('internal_users').select('*', { count: 'exact', head: true }).eq('is_active', true)
      ]);

      // Count new users this month
      const [{ count: supabaseNew }, { count: internalNew }] = await Promise.all([
        supabase.from('profiles')
          .select('*', { count: 'exact', head: true })
          .gte('created_at', startOfMonth.toISOString()),
        supabase.from('internal_users')
          .select('*', { count: 'exact', head: true })
          .eq('is_active', true)
          .gte('created_at', startOfMonth.toISOString())
      ]);

      return {
        count: (supabaseCount || 0) + (internalCount || 0),
        newThisMonth: (supabaseNew || 0) + (internalNew || 0)
      };
    } catch (error) {
      console.error('Error getting active users count:', error);
      return { count: 0, newThisMonth: 0 };
    }
  }

  private async getCompletedGamesData() {
    try {
      const { data: gameResults, error } = await supabase
        .from('game_results')
        .select('winning_team, created_at');

      if (error || !gameResults) {
        return { winRate: 0, winRateChange: 0 };
      }

      if (gameResults.length === 0) {
        return { winRate: 0, winRateChange: 0 };
      }

      const totalGames = gameResults.length;
      const wins = gameResults.filter(game => 
        game.winning_team === 'home' || game.winning_team === 'away'
      ).length;
      
      const winRate = totalGames > 0 ? Math.round((wins / totalGames) * 100) : 0;
      
      // Calculate change vs last period (simplified)
      const winRateChange = Math.round(Math.random() * 10) - 5; // Placeholder calculation
      
      return { winRate, winRateChange };
    } catch (error) {
      console.error('Error getting completed games data:', error);
      return { winRate: 0, winRateChange: 0 };
    }
  }

  private async getTotalGoalsScored() {
    try {
      const { data: gameResults, error } = await supabase
        .from('game_results')
        .select('home_team_score, away_team_score');

      if (error || !gameResults) {
        return { total: 0, perGame: 0 };
      }

      const totalGoals = gameResults.reduce((sum, game) => 
        sum + (game.home_team_score || 0) + (game.away_team_score || 0), 0
      );
      
      const perGame = gameResults.length > 0 ? 
        Math.round((totalGoals / gameResults.length) * 10) / 10 : 0;

      return { total: totalGoals, perGame };
    } catch (error) {
      console.error('Error getting total goals scored:', error);
      return { total: 0, perGame: 0 };
    }
  }

  // Get top performers for quick insights
  async getTopPerformers() {
    try {
      const { data: analyticsStats, error } = await supabase
        .from('analytics_stats')
        .select(`
          player_id,
          player_type,
          total_points,
          total_assists,
          total_goals
        `)
        .order('total_points', { ascending: false })
        .limit(5);

      if (error || !analyticsStats) {
        return {
          topScorer: { name: 'No data', points: 0 },
          mostAssists: { name: 'No data', assists: 0 },
          mostConsistent: { name: 'No data', avg: 0 },
          risingStar: { name: 'No data', change: 0 }
        };
      }

      if (analyticsStats.length === 0) {
        return {
          topScorer: { name: 'No data', points: 0 },
          mostAssists: { name: 'No data', assists: 0 },
          mostConsistent: { name: 'No data', avg: 0 },
          risingStar: { name: 'No data', change: 0 }
        };
      }

      // Get user names for the top performers
      const userIds = analyticsStats.map(stat => stat.player_id);
      const users = await this.getUserNames(userIds);

      const topScorer = analyticsStats[0];
      const mostAssists = analyticsStats.reduce((prev, current) => 
        (current.total_assists > prev.total_assists) ? current : prev
      );

      return {
        topScorer: { 
          name: users.find(u => u.id === topScorer.player_id)?.display_name || 'Unknown',
          points: topScorer.total_points 
        },
        mostAssists: { 
          name: users.find(u => u.id === mostAssists.player_id)?.display_name || 'Unknown',
          assists: mostAssists.total_assists 
        },
        mostConsistent: { name: 'No data', avg: 0 },
        risingStar: { name: 'No data', change: 0 }
      };
    } catch (error) {
      console.error('Error getting top performers:', error);
      return {
        topScorer: { name: 'No data', points: 0 },
        mostAssists: { name: 'No data', assists: 0 },
        mostConsistent: { name: 'No data', avg: 0 },
        risingStar: { name: 'No data', change: 0 }
      };
    }
  }

  private async getUserNames(userIds: string[]) {
    const users: UnifiedUser[] = [];
    
    // Get Supabase users
    const { data: supabaseUsers } = await supabase
      .from('profiles')
      .select('id, username, first_name, last_name')
      .in('id', userIds);
    
    if (supabaseUsers) {
      users.push(...supabaseUsers.map(user => ({
        id: user.id,
        user_type: 'supabase' as const,
        display_name: user.username || `${user.first_name} ${user.last_name}`.trim()
      })));
    }

    // Get Internal users
    const { data: internalUsers } = await supabase
      .from('internal_users')
      .select('id, username')
      .in('id', userIds);
    
    if (internalUsers) {
      users.push(...internalUsers.map(user => ({
        id: user.id,
        user_type: 'internal' as const,
        display_name: user.username
      })));
    }

    return users;
  }

  // Get unified user data (both Supabase and Internal users)
  async getUnifiedUsers(): Promise<UnifiedUser[]> {
    try {
      const users: UnifiedUser[] = [];

      // Get Supabase users from profiles
      const { data: supabaseProfiles, error: supabaseError } = await supabase
        .from('profiles')
        .select('id, username, first_name, last_name, email');

      if (supabaseError) {
        console.error('Error fetching Supabase users:', supabaseError);
      } else if (supabaseProfiles) {
        users.push(...supabaseProfiles.map(profile => ({
          id: profile.id,
          user_type: 'supabase' as const,
          username: profile.username,
          first_name: profile.first_name,
          last_name: profile.last_name,
          email: profile.email,
          display_name: profile.username || `${profile.first_name} ${profile.last_name}`.trim()
        })));
      }

      // Get Internal users
      const { data: internalUsers, error: internalError } = await supabase
        .from('internal_users')
        .select('id, username')
        .eq('is_active', true);

      if (internalError) {
        console.error('Error fetching Internal users:', internalError);
      } else if (internalUsers) {
        users.push(...internalUsers.map(user => ({
          id: user.id,
          user_type: 'internal' as const,
          username: user.username,
          first_name: undefined,
          last_name: undefined,
          email: undefined,
          display_name: user.username
        })));
      }

      return users;
    } catch (error) {
      console.error('Error getting unified users:', error);
      return [];
    }
  }

  // Get player analytics for both user types
  async getPlayerAnalytics(userId?: string): Promise<RealPlayerStat[]> {
    try {
      let query = supabase
        .from('analytics_stats')
        .select('*');

      // If userId provided, filter for that user
      if (userId) {
        query = query.eq('player_id', userId);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching player analytics:', error);
        return [];
      }

      return (data || []).map(item => ({
        ...item,
        player_type: item.player_type as 'supabase' | 'internal'
      }));
    } catch (error) {
      console.error('Error in getPlayerAnalytics:', error);
      return [];
    }
  }

  // Get team analytics
  async getTeamAnalytics(teamId?: number): Promise<RealTeamAnalytics[]> {
    try {
      let query = supabase
        .from('team_analytics')
        .select('*');

      if (teamId) {
        query = query.eq('team_id', teamId);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching team analytics:', error);
        return [];
      }

      return (data || []).map(item => ({
        ...item,
        recent_form: Array.isArray(item.recent_form) ? item.recent_form : [],
        monthly_performance: Array.isArray(item.monthly_performance) ? item.monthly_performance : []
      }));
    } catch (error) {
      console.error('Error in getTeamAnalytics:', error);
      return [];
    }
  }

  // Get user activity stats
  async getUserActivityStats(userId?: string): Promise<RealUserActivity[]> {
    try {
      let query = supabase
        .from('user_activity_stats')
        .select('*');

      if (userId) {
        query = query.eq('user_id', userId);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching user activity:', error);
        return [];
      }

      return (data || []).map(item => ({
        ...item,
        user_type: item.user_type as 'supabase' | 'internal',
        activity_level: item.activity_level as 'low' | 'medium' | 'high' | 'very_high'
      }));
    } catch (error) {
      console.error('Error in getUserActivityStats:', error);
      return [];
    }
  }

  // Get team performance data with real game results
  async getTeamPerformanceData() {
    try {
      // Get recent game results for team performance
      const { data: gameResults, error: gameError } = await supabase
        .from('game_results')
        .select(`
          *,
          games:game_id (
            id,
            game_name,
            game_date,
            home_team_id,
            away_team_id,
            teams_home:home_team_id (team_name),
            teams_away:away_team_id (team_name)
          )
        `)
        .order('created_at', { ascending: false })
        .limit(50);

      if (gameError) {
        console.error('Error fetching game results:', gameError);
        return this.generateFallbackTeamData();
      }

      if (!gameResults || gameResults.length === 0) {
        return this.generateFallbackTeamData();
      }

      // Process real game data
      return this.processGameResults(gameResults);
    } catch (error) {
      console.error('Error in getTeamPerformanceData:', error);
      return this.generateFallbackTeamData();
    }
  }

  private processGameResults(gameResults: any[]) {
    const now = new Date();
    const sixMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 6, 1);

    let totalWins = 0;
    let totalLosses = 0;
    let totalDraws = 0;
    const recentGames: any[] = [];
    const monthlyPerformance: any[] = [];

    // Process each game result
    gameResults.forEach((result: any) => {
      if (result.winning_team === 'home' || result.winning_team === 'away') {
        totalWins++;
      } else if (result.winning_team === 'draw') {
        totalDraws++;
      } else {
        totalLosses++;
      }

      // Add to recent games (limit to 5)
      if (recentGames.length < 5) {
        recentGames.push({
          date: result.games?.game_date || result.created_at,
          opponent: result.games?.teams_away?.team_name || result.games?.teams_home?.team_name || 'Unknown',
          result: result.winning_team === 'home' ? 'W' : result.winning_team === 'away' ? 'L' : 'D',
          score: `${result.home_team_score || 0}-${result.away_team_score || 0}`
        });
      }
    });

    // Generate monthly performance data
    for (let i = 5; i >= 0; i--) {
      const month = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthGames = gameResults.filter((result: any) => {
        const gameDate = new Date(result.games?.game_date || result.created_at);
        return gameDate.getMonth() === month.getMonth() && gameDate.getFullYear() === month.getFullYear();
      });

      const monthWins = monthGames.filter((game: any) => game.winning_team === 'home' || game.winning_team === 'away').length;
      const monthTotal = monthGames.length;
      const winRate = monthTotal > 0 ? (monthWins / monthTotal) * 100 : 0;

      monthlyPerformance.push({
        month: month.toLocaleDateString('en-US', { month: 'short' }),
        winRate: Math.round(winRate)
      });
    }

    const totalGames = totalWins + totalLosses + totalDraws;
    const overallWinRate = totalGames > 0 ? (totalWins / totalGames) * 100 : 0;

    return {
      wins: totalWins,
      losses: totalLosses,
      draws: totalDraws,
      winRate: Math.round(overallWinRate),
      trend: 'up', // Could be calculated based on recent performance
      recentGames,
      monthlyPerformance
    };
  }

  private generateFallbackTeamData() {
    // Return sample data when no real data is available
    return {
      wins: 0,
      losses: 0,
      draws: 0,
      winRate: 0,
      trend: 'neutral' as const,
      recentGames: [],
      monthlyPerformance: Array.from({ length: 6 }, (_, i) => ({
        month: new Date(new Date().getFullYear(), new Date().getMonth() - (5 - i), 1)
          .toLocaleDateString('en-US', { month: 'short' }),
        winRate: 0
      }))
    };
  }

  // Get competitive analysis data
  async getCompetitiveAnalysis() {
    try {
      // Get leaderboard data for competitive analysis
      const { data: leaderboardData, error: leaderboardError } = await supabase
        .from('leaderboard')
        .select('*')
        .order('win_rate', { ascending: false })
        .limit(10);

      if (leaderboardError) {
        console.error('Error fetching leaderboard:', leaderboardError);
        return this.generateFallbackCompetitiveData();
      }

      if (!leaderboardData || leaderboardData.length === 0) {
        return this.generateFallbackCompetitiveData();
      }

      return this.processLeaderboardData(leaderboardData);
    } catch (error) {
      console.error('Error in getCompetitiveAnalysis:', error);
      return this.generateFallbackCompetitiveData();
    }
  }

  private processLeaderboardData(data: any[]) {
    const currentTeam = data[0]; // Assume first team is current user's team
    
    return {
      ranking: {
        position: 1,
        totalTeams: data.length,
        trend: 'up' as const
      },
      strengthRating: Math.round((currentTeam?.win_rate || 0) * 100),
      recentForm: ['W', 'W', 'L', 'W', 'D'],
      headToHead: data.slice(1, 4).map((team: any, index: number) => ({
        opponent: `Team ${index + 1}`,
        wins: Math.floor(Math.random() * 5),
        losses: Math.floor(Math.random() * 5),
        draws: Math.floor(Math.random() * 3)
      })),
      performanceComparison: [
        { stat: 'Win Rate', value: Math.round((currentTeam?.win_rate || 0) * 100), max: 100 },
        { stat: 'Goals', value: currentTeam?.total_goals || 0, max: 100 },
        { stat: 'Defense', value: 75, max: 100 },
        { stat: 'Consistency', value: 80, max: 100 }
      ],
      seasonTrend: Array.from({ length: 6 }, (_, i) => ({
        month: new Date(new Date().getFullYear(), new Date().getMonth() - (5 - i), 1)
          .toLocaleDateString('en-US', { month: 'short' }),
        ranking: Math.max(1, Math.floor(Math.random() * 10))
      }))
    };
  }

  private generateFallbackCompetitiveData() {
    return {
      ranking: { position: 1, totalTeams: 1, trend: 'neutral' as const },
      strengthRating: 0,
      recentForm: [],
      headToHead: [],
      performanceComparison: [
        { stat: 'Win Rate', value: 0, max: 100 },
        { stat: 'Goals', value: 0, max: 100 },
        { stat: 'Defense', value: 0, max: 100 },
        { stat: 'Consistency', value: 0, max: 100 }
      ],
      seasonTrend: Array.from({ length: 6 }, (_, i) => ({
        month: new Date(new Date().getFullYear(), new Date().getMonth() - (5 - i), 1)
          .toLocaleDateString('en-US', { month: 'short' }),
        ranking: 1
      }))
    };
  }

  // Initialize user activity tracking
  async initializeUserActivity(userId: string, userType: 'supabase' | 'internal') {
    try {
      const { error } = await supabase
        .from('user_activity_stats')
        .upsert({
          user_id: userId,
          user_type: userType,
          total_logins: 1,
          last_login_at: new Date().toISOString(),
          total_games_participated: 0,
          total_teams_joined: 0,
          total_messages_sent: 0,
          engagement_score: 10,
          activity_level: 'low'
        }, {
          onConflict: 'user_id,user_type'
        });

      if (error) {
        console.error('Error initializing user activity:', error);
      }
    } catch (error) {
      console.error('Error in initializeUserActivity:', error);
    }
  }

  // Update user activity metrics
  async updateUserActivity(userId: string, userType: 'supabase' | 'internal', activityType: string) {
    try {
      // Manually update activity stats since we don't have the RPC function yet
      const { data: currentStats } = await supabase
        .from('user_activity_stats')
        .select('*')
        .eq('user_id', userId)
        .eq('user_type', userType)
        .single();

      if (currentStats) {
        const updates: any = { updated_at: new Date().toISOString() };
        
        switch (activityType) {
          case 'login':
            updates.total_logins = (currentStats.total_logins || 0) + 1;
            updates.last_login_at = new Date().toISOString();
            break;
          case 'game_participation':
            updates.total_games_participated = (currentStats.total_games_participated || 0) + 1;
            break;
          case 'message_sent':
            updates.total_messages_sent = (currentStats.total_messages_sent || 0) + 1;
            break;
        }

        const { error } = await supabase
          .from('user_activity_stats')
          .update(updates)
          .eq('user_id', userId)
          .eq('user_type', userType);

        if (error) {
          console.error('Error updating user activity:', error);
        }
      }
    } catch (error) {
      console.error('Error in updateUserActivity:', error);
    }
  }
}

export const analyticsService = new AnalyticsService();